/**
 * Local port of use-react-router-breadcrumbs (MIT).
 * https://github.com/icd2k3/use-react-router-breadcrumbs
 */
import { createElement } from "react";
import { matchPath, useLocation } from "react-router";

import { emptyString } from "../../../../constants";
import {
  breadcrumbPathConstants,
  breadcrumbTypeConstants,
  breadcrumbElementConstants,
  breadcrumbIndexConstants,
  breadcrumbScoreConstants,
  breadcrumbErrorMessages,
} from "./useBreadcrumbs.constants";

const {
  PATH_SEPARATOR,
  QUERY_SEPARATOR,
  SPLAT,
  SPACE,
  HOME_LABEL,
  NO_BREADCRUMB_KEY,
} = breadcrumbPathConstants;

const { STRING: TYPE_STRING } = breadcrumbTypeConstants;
const { SPAN: ELEMENT_SPAN } = breadcrumbElementConstants;
const {
  FIRST_CHAR,
  FIRST_SEGMENT,
  LAST_ITEM_OFFSET,
  PARENT_META_OFFSET,
  EQUAL_SCORE,
  EMPTY_LENGTH,
} = breadcrumbIndexConstants;
const {
  DYNAMIC_SEGMENT,
  INDEX_ROUTE,
  EMPTY_SEGMENT,
  STATIC_SEGMENT,
  SPLAT_PENALTY,
} = breadcrumbScoreConstants;

const joinPaths = (paths) =>
  paths.join(PATH_SEPARATOR).replace(/\/\/+/g, PATH_SEPARATOR);

const paramRe = /^:\w+$/;
const isSplat = (s) => s === SPLAT;

const computeScore = (path, index) => {
  const segments = path.split(PATH_SEPARATOR);
  let initialScore = segments.length;
  if (segments.some(isSplat)) {
    initialScore += SPLAT_PENALTY;
  }

  if (index) {
    initialScore += INDEX_ROUTE;
  }

  return segments
    .filter((s) => !isSplat(s))
    .reduce((score, segment) => {
      if (paramRe.test(segment)) {
        return score + DYNAMIC_SEGMENT;
      }
      if (segment === emptyString) {
        return score + EMPTY_SEGMENT;
      }
      return score + STATIC_SEGMENT;
    }, initialScore);
};

const compareIndexes = (a, b) => {
  const siblings =
    a.length === b.length &&
    a.slice(FIRST_SEGMENT, -LAST_ITEM_OFFSET).every((n, i) => n === b[i]);
  return siblings
    ? a[a.length - LAST_ITEM_OFFSET] - b[b.length - LAST_ITEM_OFFSET]
    : EQUAL_SCORE;
};

const flattenRoutes = (
  routes,
  branches = [],
  parentsMeta = [],
  parentPath = emptyString,
) => {
  routes.forEach((route, index) => {
    if (
      typeof route.path !== TYPE_STRING &&
      !route.index &&
      !route.children?.length
    ) {
      throw new Error(breadcrumbErrorMessages.PATH_OR_INDEX_REQUIRED);
    }
    if (route.path && route.index) {
      throw new Error(breadcrumbErrorMessages.PATH_AND_INDEX);
    }
    const meta = {
      relativePath: route.path || emptyString,
      childrenIndex: index,
      route,
    };

    if (meta.relativePath.charAt(FIRST_CHAR) === PATH_SEPARATOR) {
      if (!meta.relativePath.startsWith(parentPath)) {
        throw new Error(breadcrumbErrorMessages.ABSOLUTE_CHILD_PATH);
      }
      meta.relativePath = meta.relativePath.slice(parentPath.length);
    }

    const path = joinPaths([parentPath, meta.relativePath]);
    const routesMeta = parentsMeta.concat(meta);

    if (route.children && route.children.length > EMPTY_LENGTH) {
      if (route.index) {
        throw new Error(breadcrumbErrorMessages.INDEX_HAS_CHILDREN);
      }
      flattenRoutes(route.children, branches, routesMeta, path);
    }

    branches.push({
      path,
      score: computeScore(path, route.index),
      routesMeta,
    });
  });
  return branches;
};

const rankRouteBranches = (branches) =>
  [...branches].sort((a, b) =>
    a.score !== b.score
      ? b.score - a.score
      : compareIndexes(
          a.routesMeta.map((meta) => meta.childrenIndex),
          b.routesMeta.map((meta) => meta.childrenIndex),
        ),
  );

const NO_BREADCRUMB = Symbol(NO_BREADCRUMB_KEY);

export const humanize = (str) =>
  str
    .replace(/^[\s_]+|[\s_]+$/g, emptyString)
    .replace(/[-_\s]+/g, SPACE)
    .replace(/^[a-z]/, (m) => m.toUpperCase());

const render = ({ breadcrumb: Breadcrumb, match, location, props }) => {
  const componentProps = {
    match,
    location,
    key: match.pathname,
    ...(props || {}),
  };

  return {
    ...componentProps,
    breadcrumb:
      typeof Breadcrumb === TYPE_STRING
        ? createElement(ELEMENT_SPAN, { key: componentProps.key }, Breadcrumb)
        : createElement(Breadcrumb, componentProps),
  };
};

const getDefaultBreadcrumb = ({
  currentSection,
  location,
  pathSection,
  defaultFormatter,
}) => {
  const match = matchPath(
    {
      end: true,
      path: pathSection,
    },
    pathSection,
  );

  return render({
    breadcrumb: defaultFormatter
      ? defaultFormatter(currentSection)
      : humanize(currentSection),
    match,
    location,
  });
};

const getBreadcrumbMatch = ({
  currentSection,
  disableDefaults,
  excludePaths,
  defaultFormatter,
  location,
  pathSection,
  branches,
}) => {
  let breadcrumb;

  const getIsPathExcluded = (path) =>
    matchPath(
      {
        path,
        end: true,
      },
      pathSection,
    ) != null;

  if (excludePaths && excludePaths.some(getIsPathExcluded)) {
    return NO_BREADCRUMB;
  }

  branches.some(({ path, routesMeta }) => {
    const { route } = routesMeta[routesMeta.length - LAST_ITEM_OFFSET];
    let userProvidedBreadcrumb = route.breadcrumb;
    if (!userProvidedBreadcrumb && route.index) {
      const parentMeta = routesMeta[routesMeta.length - PARENT_META_OFFSET];
      if (parentMeta && parentMeta.route.breadcrumb) {
        userProvidedBreadcrumb = parentMeta.route.breadcrumb;
      }
    }

    const { caseSensitive, props } = route;
    const match = matchPath(
      {
        path,
        end: true,
        caseSensitive,
      },
      pathSection,
    );

    if (match && userProvidedBreadcrumb === null) {
      breadcrumb = NO_BREADCRUMB;
      return true;
    }

    if (match) {
      if (!userProvidedBreadcrumb && disableDefaults) {
        breadcrumb = NO_BREADCRUMB;
        return true;
      }

      breadcrumb = render({
        breadcrumb:
          userProvidedBreadcrumb ||
          (defaultFormatter
            ? defaultFormatter(currentSection)
            : humanize(currentSection)),
        match: { ...match, route },
        location,
        props,
      });
      return true;
    }
    return false;
  });

  if (breadcrumb) {
    return breadcrumb;
  }

  if (disableDefaults) {
    return NO_BREADCRUMB;
  }

  return getDefaultBreadcrumb({
    pathSection,
    currentSection:
      pathSection === PATH_SEPARATOR ? HOME_LABEL : currentSection,
    location,
    defaultFormatter,
  });
};

export const getBreadcrumbs = ({ routes, location, options = {} }) => {
  const { pathname } = location;

  const branches = rankRouteBranches(flattenRoutes(routes));
  const breadcrumbs = [];
  pathname
    .split(QUERY_SEPARATOR)[FIRST_SEGMENT]
    .split(PATH_SEPARATOR)
    .reduce((previousSection, currentSection, index) => {
      const pathSection = !currentSection
        ? PATH_SEPARATOR
        : `${previousSection}${PATH_SEPARATOR}${currentSection}`;

      if (pathSection === PATH_SEPARATOR && index !== FIRST_SEGMENT) {
        return emptyString;
      }

      const breadcrumb = getBreadcrumbMatch({
        currentSection,
        location,
        pathSection,
        branches,
        ...options,
      });

      if (breadcrumb !== NO_BREADCRUMB) {
        breadcrumbs.push(breadcrumb);
      }

      return pathSection === PATH_SEPARATOR ? emptyString : pathSection;
    }, emptyString);

  return breadcrumbs;
};

export const useBreadcrumbs = (routes, options) =>
  getBreadcrumbs({
    routes: routes || [],
    location: useLocation(),
    options,
  });
